import React, { useState, useEffect } from 'react';
import { Bot, Calendar, Clock, FileText, Send, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const AIAssistedAbsenceRequest = ({ onRequestCreated, onClose }) => {
  const [step, setStep] = useState('chat'); // 'chat', 'review', 'submitted'
  const [chatMessages, setChatMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [extractedData, setExtractedData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Start with a welcome message
    setChatMessages([{
      id: 1,
      type: 'ai',
      content: "Hi! I'll help you request time off. You can tell me about your request in natural language, like:\n\n• \"I need to take tomorrow off for a doctor's appointment\"\n• \"I want to request vacation from March 15-20\"\n• \"I'm feeling sick and need to take today off\"\n\nWhat would you like to request?",
      timestamp: new Date()
    }]);
  }, []);

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await apiService.makeRequest('/api/ai/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: message,
          context: { workflow: 'absence_request' }
        })
      });

      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.response,
        timestamp: new Date(),
        actions: response.actions || []
      };

      setChatMessages(prev => [...prev, aiMessage]);

      // Check if we have enough information to create the request
      if (response.actions && response.actions.some(action => action.type === 'create_absence')) {
        const createAction = response.actions.find(action => action.type === 'create_absence');
        setExtractedData(createAction.data);
        setStep('review');
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: "I'm sorry, I encountered an error. Please try again or fill out the form manually.",
        timestamp: new Date(),
        isError: true
      };
      setChatMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const submitAbsenceRequest = async () => {
    try {
      setIsSubmitting(true);
      
      const absenceData = {
        start_date: extractedData.start_date,
        end_date: extractedData.end_date,
        leave_type: extractedData.leave_type || 'personal',
        reason: extractedData.reason,
        total_hours: extractedData.total_hours || 8,
        documentation_required: extractedData.documentation_required || false,
        created_via_ai: true
      };

      const result = await apiService.createAbsence(absenceData);
      
      setStep('submitted');
      toast({
        title: 'Request Submitted',
        description: 'Your absence request has been submitted for approval.'
      });

      // Refresh notifications after creating absence
      window.dispatchEvent(new CustomEvent('notifications:refresh'));
      setTimeout(async () => {
        try {
          await apiService.get('/api/notifications/unread-count');
        } catch (err) {
          console.error('Failed to refresh notification count:', err);
        }
      }, 1000);

      if (onRequestCreated) {
        onRequestCreated(result);
      }

    } catch (error) {
      console.error('Failed to submit absence request:', error);
      toast({
        title: 'Submission Failed',
        description: 'Failed to submit your request. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const editData = (field, value) => {
    setExtractedData(prev => ({ ...prev, [field]: value }));
  };

  if (step === 'submitted') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Request Submitted!</h3>
          <p className="text-gray-600 mb-6">
            Your absence request has been submitted and is pending approval. You'll receive a notification once it's reviewed.
          </p>
          <div className="flex justify-center space-x-4">
            <Button onClick={onClose} variant="outline">
              Close
            </Button>
            <Button 
              onClick={() => {
                setStep('chat');
                setChatMessages([{
                  id: 1,
                  type: 'ai',
                  content: "Would you like to submit another absence request?",
                  timestamp: new Date()
                }]);
                setExtractedData({});
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              New Request
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === 'review') {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Bot className="w-5 h-5 mr-2 text-yellow-600" />
            Review Your Request
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              I've extracted the following information from our conversation. Please review and make any necessary changes:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={extractedData.start_date || ''}
                onChange={(e) => editData('start_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={extractedData.end_date || ''}
                onChange={(e) => editData('end_date', e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="leave_type">Leave Type</Label>
            <select
              id="leave_type"
              value={extractedData.leave_type || 'personal'}
              onChange={(e) => editData('leave_type', e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            >
              <option value="sick">Sick Leave</option>
              <option value="vacation">Vacation</option>
              <option value="personal">Personal Day</option>
              <option value="emergency">Emergency</option>
              <option value="bereavement">Bereavement</option>
            </select>
          </div>

          <div>
            <Label htmlFor="reason">Reason</Label>
            <Textarea
              id="reason"
              value={extractedData.reason || ''}
              onChange={(e) => editData('reason', e.target.value)}
              placeholder="Brief description of the reason for your absence"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="total_hours">Total Hours</Label>
            <Input
              id="total_hours"
              type="number"
              step="0.5"
              min="0.5"
              max="8"
              value={extractedData.total_hours ?? ''}
              placeholder="8"
              onChange={(e) => {
                const value = e.target.value;
                editData('total_hours', value === '' ? '' : parseFloat(value));
              }}
            />
          </div>

          {extractedData.documentation_required && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <FileText className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-800">Documentation Required</span>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                This type of absence may require supporting documentation. Please prepare any necessary documents.
              </p>
            </div>
          )}

          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={() => setStep('chat')}
            >
              Back to Chat
            </Button>
            <Button
              onClick={submitAbsenceRequest}
              disabled={isSubmitting || !extractedData.start_date || !extractedData.end_date}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-2xl mx-auto h-96 flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center">
          <Bot className="w-5 h-5 mr-2 text-yellow-600" />
          AI-Assisted Absence Request
        </CardTitle>
      </CardHeader>

      <CardContent className="flex-1 p-0 overflow-hidden">
        <div className="h-full p-4 overflow-y-auto">
          <div className="space-y-4">
            {chatMessages.map((message) => (
              <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.type === 'user' 
                    ? 'bg-yellow-500 text-black' 
                    : message.isError 
                      ? 'bg-red-50 text-red-800 border border-red-200'
                      : 'bg-gray-100 text-gray-900'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  <div className="mt-1">
                    <span className="text-xs opacity-70">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 px-4 py-2 rounded-lg">
                  <div className="flex items-center space-x-1">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-xs text-gray-500 ml-2">Assistant is typing...</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>

      <div className="p-4 border-t">
        <form onSubmit={handleSubmit} className="flex space-x-2">
          <Input
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Describe your time off request..."
            disabled={isTyping}
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isTyping || !inputValue.trim()}
            className="bg-yellow-500 hover:bg-yellow-600 text-black"
          >
            {isTyping ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default AIAssistedAbsenceRequest;